import Phaser from 'phaser';
import { menuTowerTexture } from '../config/unlocks';
import type { TowerType } from '../core/types';
import { BALANCE } from '../config/balance';
import { TOWER_NAMES, TOWER_TUTORIALS } from '../config/displayNames';
import { rangedStatOf, towerSpecial, towerStatLine } from './towerInfo';

/**
 * The "?" tower guide — a paginated modal listing every currently unlocked
 * tower with its base cost, plain-English stats, special effect, and blurb.
 *
 * Shared by HudScene (in-game "?", which pauses GameScene around it) and
 * LevelSelectScene (episode-picker "?"). The host scene owns any pause/resume
 * side-effects via the onClose callback; this module only builds the modal.
 */

/** Towers shown per guide page. */
const GUIDE_PER_PAGE = 5;

/**
 * Widest a row's stat line may draw before it is scaled down. Rows are 760px
 * and the text starts 86px in, so this keeps the longest line clear of the
 * panel's right border.
 */
const STAT_LINE_MAX_W = 620;

/** Scenes with a guide currently open — prevents stacked modals per scene. */
const openScenes = new WeakSet<Phaser.Scene>();

/**
 * Open the guide over `scene` for `types`. Returns false (and does nothing)
 * if that scene already shows one. `onClose` fires after teardown.
 */
export function openTowerGuide(
  scene: Phaser.Scene,
  types: TowerType[],
  opts: { onClose?: () => void } = {},
): boolean {
  if (openScenes.has(scene)) return false;
  openScenes.add(scene);
  // If the scene shuts down with the modal open (abandon, scene jump), the
  // objects die with it — drop the guard so the next visit can open again.
  scene.events.once('shutdown', () => openScenes.delete(scene));

  const W = 1280;
  const H = 720;
  const D = 100; // above everything the host scene draws
  let page = 0;

  const objs: Phaser.GameObjects.GameObject[] = [];
  let rowObjs: Phaser.GameObjects.GameObject[] = [];

  objs.push(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
      .setInteractive() // swallow clicks behind the modal
      .setDepth(D),
  );
  objs.push(
    scene.add
      .rectangle(W / 2, H / 2 + 8, 800, 620, 0x1a1726, 1)
      .setStrokeStyle(3, 0xf5c518)
      .setDepth(D + 1),
  );
  objs.push(
    scene.add
      .text(W / 2, 88, 'TOWER GUIDE', { fontSize: '22px', color: '#f5c518', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(D + 2),
  );

  /** (Re)draw the rows for the current page. */
  const renderPage = (pageLabel: Phaser.GameObjects.Text | null, pages: number): void => {
    rowObjs.forEach((o) => o.destroy());
    rowObjs = [];
    pageLabel?.setText(`${page + 1} / ${pages}`);

    const start = page * GUIDE_PER_PAGE;
    const shown = types.slice(start, start + GUIDE_PER_PAGE);
    const left = W / 2 - 370; // panel is 800 wide, centred; rows are 760
    shown.forEach((type, i) => {
      const y = 158 + i * 92;
      const tiers = BALANCE.towers[type];
      const spec = tiers.tier1;
      // The top of the range is the Evolution tier where a tower has one, so
      // the guide describes the tower rather than this player's progress.
      const topSpec = tiers.tier4 ?? tiers.tier3;
      rowObjs.push(
        scene.add
          .rectangle(W / 2, y + 26, 760, 84, 0x241f33, 1)
          .setStrokeStyle(1, 0x44485a)
          .setDepth(D + 1.5),
      );
      const artKey = scene.textures.exists(menuTowerTexture(type))
        ? menuTowerTexture(type)
        : 'tower-patty-1';
      rowObjs.push(
        scene.add
          .image(left + 40, y + 26, artKey)
          .setDisplaySize(60, 60)
          .setDepth(D + 2),
      );
      rowObjs.push(
        scene.add
          .text(left + 86, y - 4, TOWER_NAMES[type], {
            fontSize: '17px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setDepth(D + 2),
      );
      // Cost — right-aligned to the row's inner edge.
      rowObjs.push(
        scene.add
          .text(W / 2 + 354, y - 2, `$${spec.cost}`, { fontSize: '15px', color: '#f5c518' })
          .setOrigin(1, 0)
          .setDepth(D + 2),
      );
      const special = towerSpecial(spec, type, rangedStatOf(spec));
      const statLine = scene.add
        .text(
          left + 86,
          y + 20,
          towerStatLine(spec, topSpec) + (special ? `  ·  ${special}` : ''),
          {
            fontSize: '14px',
            color: '#9fd8ff',
          },
        )
        .setDepth(D + 2);
      // Scale a long line down rather than let it run out of the panel — same
      // guard the shop rows use. Gunner Hambi's line carries two extra clauses
      // (the crit and headshot bands) and overflowed the frame without this.
      if (statLine.width > STAT_LINE_MAX_W) statLine.setScale(STAT_LINE_MAX_W / statLine.width);
      rowObjs.push(statLine);
      rowObjs.push(
        scene.add
          .text(left + 86, y + 42, TOWER_TUTORIALS[type], {
            fontSize: '12px',
            color: '#9aa6ad',
            wordWrap: { width: 640 },
          })
          .setDepth(D + 2),
      );
    });
  };

  // Page controls — top-right of the panel, beside the title, well away from
  // the rows and the Got it! button (they used to crowd the last row).
  const pages = Math.ceil(types.length / GUIDE_PER_PAGE);
  let pageLabel: Phaser.GameObjects.Text | null = null;
  if (pages > 1) {
    pageLabel = scene.add
      .text(W / 2 + 336, 88, '', { fontSize: '15px', color: '#f5c518' })
      .setOrigin(0.5)
      .setDepth(D + 2);
    objs.push(pageLabel);
    const mkArrow = (x: number, glyph: string, delta: number) => {
      // Padding inflates the text bounds — Phaser's pointer hit area — so the
      // tap target is finger-sized instead of the few-pixel-wide glyph.
      const arrow = scene.add
        .text(x, 88, glyph, {
          fontSize: '34px',
          color: '#ffffff',
          fontStyle: 'bold',
          padding: { x: 10, y: 6 },
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2);
      arrow.on('pointerup', () => {
        const next = page + delta;
        if (next >= 0 && next < pages) {
          page = next;
          renderPage(pageLabel, pages);
        }
      });
      objs.push(arrow);
    };
    mkArrow(W / 2 + 288, '‹', -1);
    mkArrow(W / 2 + 384, '›', +1);
  }

  // Close button — kept well inside the panel's bottom edge (678).
  const btn = scene.add
    .rectangle(W / 2, 640, 200, 46, 0x1a6b1a)
    .setStrokeStyle(2, 0x4caf50)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 2);
  objs.push(btn);
  objs.push(
    scene.add
      .text(W / 2, 640, 'Got it!', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(D + 3),
  );
  btn.on('pointerup', () => {
    rowObjs.forEach((o) => o.destroy());
    rowObjs = [];
    objs.forEach((o) => o.destroy());
    openScenes.delete(scene);
    opts.onClose?.();
  });

  renderPage(pageLabel, pages);
  return true;
}
